import { apiClient } from './api';
import {
  Assignment,
  CreateAssignmentRequest,
  AssignmentFilters,
  ApiResponse,
} from '../types';

class AssignmentsService {
  private endpoint = '/admin/assignments';

  /**
   * Fetch all assignments with optional filters
   */
  async getAssignments(filters?: AssignmentFilters): Promise<Assignment[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.date) params.append('date', filters.date);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.driverId) params.append('driverId', filters.driverId);

      const query = params.toString();
      const url = query ? `${this.endpoint}?${query}` : this.endpoint;

      const response = await apiClient.get<any>(url);
      return response.data.assignments || [];
    } catch (error) {
      console.error('Error fetching assignments:', error);
      throw error;
    }
  }

  /**
   * Fetch a single assignment by ID
   */
  async getAssignment(assignmentId: string): Promise<Assignment> {
    try {
      const response = await apiClient.get<any>(
        `${this.endpoint}/${assignmentId}`
      );
      if (!response.data) {
        throw new Error('Assignment not found');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment:', error);
      throw error;
    }
  }

  /**
   * Create a new assignment
   */
  async createAssignment(request: CreateAssignmentRequest): Promise<Assignment> {
    try {
      const response = await apiClient.post<any>(
        this.endpoint,
        request
      );
      if (!response.data) {
        throw new Error('Failed to create assignment');
      }
      return response.data;
    } catch (error) {
      console.error('Error creating assignment:', error);
      throw error;
    }
  }

  /**
   * Update an assignment
   */
  async updateAssignment(
    assignmentId: string,
    request: Partial<CreateAssignmentRequest>
  ): Promise<Assignment> {
    try {
      const response = await apiClient.put<any>(
        `${this.endpoint}/${assignmentId}`,
        request
      );
      if (!response.data) {
        throw new Error('Failed to update assignment');
      }
      return response.data;
    } catch (error) {
      console.error('Error updating assignment:', error);
      throw error;
    }
  }

  /**
   * Delete an assignment
   */
  async deleteAssignment(assignmentId: string): Promise<void> {
    try {
      await apiClient.delete(`${this.endpoint}/${assignmentId}`);
    } catch (error) {
      console.error('Error deleting assignment:', error);
      throw error;
    }
  }

  /**
   * Get assignments by driver
   */
  async getAssignmentsByDriver(driverId: string): Promise<Assignment[]> {
    return this.getAssignments({ driverId });
  }

  /**
   * Get assignments by date
   */
  async getAssignmentsByDate(date: string): Promise<Assignment[]> {
    return this.getAssignments({ date });
  }

  /**
   * Get assignments by status
   */
  async getAssignmentsByStatus(status: string): Promise<Assignment[]> {
    return this.getAssignments({ status });
  }
}

export const assignmentsService = new AssignmentsService();
